import type { FC } from "react";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import { OPEN_NOW_CATEGORY, OPEN_NOW_FILTER_OBJ } from "@locators/components/shared/utils";
import classNames from "classnames";

import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { ReplayIcon } from "@jti/brand-icons/replay";
import { Button } from "@jti/core-components/components/Button";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import { DropdownSelect } from "@jti/ui/components/Inputs";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFilterButton } from "../StoreLocatorFilterButton";

import styles from "./StoreLocatorFilters.module.scss";

const MAX_MOBILE_WIDTH = 479;

export const StoreLocatorFilters: FC = () => {
  const { __ } = useTranslations();
  const {
    selectedFilters,
    filters,
    filtersEnabled,
    handleResetFilters,
    handleToggleFilters,
    isFiltersAvailable,
    isFiltersOpened,
    isOpenNowFilterEnabled,
    applyFilters,
    changeFilters,
    checkFiltersStatus,
    handleDropdownRestart,
    handleDropdownSelect,
    handleApplyOpenNowCheckbox,
    activeFilters,
  } = useStoreLocatorContext();

  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH });

  const selectedFiltersNumber = useMemo(
    () => (selectedFilters ? Object.values(selectedFilters)?.filter(({ selected }) => selected).length : 0),
    [selectedFilters]
  );

  if (!isFiltersAvailable || !filtersEnabled) {
    return null;
  }

  if (isMobile) {
    return (
      <div>
        <StoreLocatorFilterButton
          isFiltersAvailable={isFiltersAvailable}
          isFiltersOpened={isFiltersOpened}
          onToggleFilters={handleToggleFilters}
          selectedFiltersNumber={selectedFiltersNumber}
        />
        {isFiltersOpened &&
          createPortal(
            <div className={styles.filtersMobileWrapper}>
              <div className={styles.filtersHeaderContainer}>
                <button className={styles.filtersHeaderButton} onClick={handleToggleFilters}>
                  <ChevronBackwardIcon />
                  <h2>{__("headerlinks.button.findstore")}</h2>
                </button>
              </div>
              <div className={styles.filtersTitleContainer}>
                <h2 className={styles.filtersTitle} data-testid="storeLocatorFiltersTitle">
                  {__("storelocator.filtersby")}
                </h2>
                {!!selectedFiltersNumber && (
                  <button
                    className={styles.resetButton}
                    onClick={handleResetFilters}
                    aria-label={__("storelocator.reset_filters")}
                  >
                    <ReplayIcon className={styles.resetButtonIcon} height="20px" width="20px" aria-hidden="true" />
                    {__("storelocator.reset_filters")}
                  </button>
                )}
              </div>

              <div className={styles.filtersList}>
                {isOpenNowFilterEnabled && (
                  <div className={styles.filtersCategory}>
                    <h3 className={styles.filtersCategoryTitle}>{__("storelocator.filters.category.open_now")}</h3>
                    <div className={styles.checkboxContainer}>
                      <Checkbox
                        label={__("store_locator.popup.shop_status.open")}
                        checked={checkFiltersStatus(OPEN_NOW_CATEGORY, OPEN_NOW_CATEGORY)}
                        onCheckedChange={() => {
                          changeFilters(OPEN_NOW_CATEGORY, OPEN_NOW_FILTER_OBJ);
                        }}
                      />
                    </div>
                  </div>
                )}
                {filters?.map((filterCategory) => (
                  <div className={styles.filtersCategory} key={filterCategory.name}>
                    <div className={styles.filtersCategoryTitle}>{filterCategory.name}</div>
                    <div className={styles.filtersCategoryItems}>
                      {filterCategory.filters.map((filter) => (
                        <div className={styles.filtersCategoryItem} key={filter.key}>
                          <div className={styles.checkboxContainer}>
                            <Checkbox
                              label={filter.name}
                              checked={checkFiltersStatus(filterCategory.key, filter.key)}
                              onCheckedChange={() => {
                                changeFilters(filterCategory.key, filter);
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.filterButtonContainer}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    applyFilters();
                  }}
                >
                  {__("storelocator.button.apply")}
                </Button>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }

  return (
    <div className={styles.filtersWrapper} data-testid="storeLocatorFilters">
      <div className={styles.filtersContainer}>
        <h2 className={styles.filtersTitle} data-testid="storeLocatorFiltersTitle">
          {__("storelocator.filtersby")}
        </h2>
        {isOpenNowFilterEnabled && (
          <Checkbox
            label={__("store_locator.popup.shop_status.open")}
            checked={checkFiltersStatus(OPEN_NOW_CATEGORY, OPEN_NOW_CATEGORY)}
            onCheckedChange={handleApplyOpenNowCheckbox}
          />
        )}
        <div className={classNames(styles.dropdownSelectContainer)}>
          {filters?.map((filterCategory, i) => {
            return (
              <DropdownSelect
                dataTestId={`storeLocatorFiltersDropdown-${i}`}
                containerClassName={styles.dropdownSelect}
                options={filterCategory.filters}
                label={filterCategory.name}
                onChange={handleDropdownSelect.bind(this, filterCategory.key)}
                onRestart={handleDropdownRestart.bind(this, filterCategory.key)}
                value={activeFilters[filterCategory.key] || []}
                onApply={applyFilters}
                key={filterCategory.key}
                applyButtonText={__("storelocator.button.apply")}
                restartButtonText={__("storelocator.button.cancel")}
              />
            );
          })}
        </div>
      </div>
      {!!selectedFiltersNumber && (
        <button className={classNames(styles.resetButton, styles.resetButtonMobile)} onClick={handleResetFilters}>
          <ReplayIcon width="20px" height="20px" />
          {__("storelocator.reset_filters")}
        </button>
      )}
    </div>
  );
};
